import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

import { loadPrism, type AnimatorPreviewApi, type AnimatorPreviewFrame, type AnimatorPreviewGeometry } from '../../engine/prism.js';
import { readProjectFile } from '../../engine/host.js';
import { useProjectFiles } from '../../state/project-files.js';
import { useWorkspace } from '../../state/workspace.js';
import { IconButton, NumberField, SelectField, Toggle } from '../ui/controls.js';
import type { Transition } from './controller-doc.js';
import { PreviewViewport } from './PreviewViewport.js';
import {
  guessModel,
  previewModelKey,
  stateNormalized,
  transitionSample,
  transitionTimeline,
  type PreviewValue,
  type StateTiming,
  type TransitionTimeline,
} from './preview-model.js';
import type { ControllerDoc } from './controller-doc.js';

/** What the selection previews. */
export type PreviewTarget =
  | { kind: 'state'; layer: number; state: string }
  | { kind: 'transition'; layer: number; from: string; to: string; t: Transition };

type Mode = 'selection' | 'live';
const SPEEDS = ['0.1', '0.25', '0.5', '1', '1.5', '2'];
const IMAGE_KEYS = ['map', 'normalMap', 'metallicRoughnessMap', 'ormMap', 'emissiveMap', 'occlusionMap'];

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

/**
 * Animator window preview: a project model posed by this controller. In
 * "Selection" mode it plays the selected state (blend trees follow the
 * preview parameters) or the selected transition on a scrubbable timeline;
 * "State machine" runs the whole controller live (transitions, triggers).
 */
export function PreviewPane({
  path,
  content,
  doc,
  target,
  values,
  onValues,
}: {
  path: string;
  content: string;
  doc: ControllerDoc;
  target: PreviewTarget | null;
  values: Record<string, PreviewValue>;
  /** Engine read-back (triggers consumed by live transitions). */
  onValues: (values: Record<string, PreviewValue>) => void;
}) {
  const projectId = useWorkspace((s) => s.projectId);
  const entries = useProjectFiles((s) => s.entries);
  const models = useMemo(
    () => entries.filter((e) => e.kind === 'file' && /\.(glb|model\.json)$/i.test(e.name)).map((e) => e.path).sort((a, b) => a.localeCompare(b)),
    [entries],
  );
  // Read through a ref: file-list refreshes (e.g. this controller's autosave) must not reload the preview.
  const filesRef = useRef(entries);
  filesRef.current = entries;
  const exists = useCallback((p: string) => filesRef.current.some((e) => e.path === p), []);

  const engine = useRef<AnimatorPreviewApi | null>(null);
  const [api, setApi] = useState<AnimatorPreviewApi | null>(null);
  const ready = api !== null;
  const [status, setStatus] = useState('Loading engine…');
  const [modelError, setModelError] = useState('');
  const [issues, setIssues] = useState<string[]>([]);
  const [geometry, setGeometry] = useState<AnimatorPreviewGeometry | null>(null);
  const [pose, setPose] = useState<{ positions: Float32Array | null; frame: AnimatorPreviewFrame | null; version: number }>({ positions: null, frame: null, version: 0 });
  const [controllerVersion, setControllerVersion] = useState(0);

  const storedKey = projectId ? previewModelKey(projectId, path) : null;
  const [model, setModelState] = useState<string | null>(() => (storedKey ? storage()?.getItem(storedKey) ?? null : null));
  const chosen = model && models.includes(model) ? model : guessModel(doc, models);
  const setModel = (m: string) => {
    setModelState(m);
    if (storedKey) storage()?.setItem(storedKey, m);
  };

  const [mode, setMode] = useState<Mode>('selection');
  const [playing, setPlaying] = useState(true);
  const [loop, setLoop] = useState(true);
  const [speed, setSpeed] = useState(1);
  const [time, setTime] = useState(0);
  const [showJoints, setShowJoints] = useState(false);
  const [reload, setReload] = useState(0);

  // ---- engine lifetime (a fresh instance per reload: re-reads model and clips) --
  useEffect(() => {
    let alive = true;
    let created: AnimatorPreviewApi | null = null;
    void loadPrism().then((module) => {
      if (!alive) return;
      if (!module?.AnimatorPreview) {
        setStatus(module ? 'The animator preview needs the current Prism Engine Cloud engine build. Rebuild the engine and reload.' : 'The Prism Engine Cloud engine is not available.');
        return;
      }
      created = new module.AnimatorPreview();
      engine.current = created;
      setApi(created);
      setStatus('');
    });
    return () => {
      alive = false;
      if (engine.current === created) engine.current = null;
      created?.free();
      setApi(null);
      setGeometry(null);
    };
  }, [reload]);

  // ---- model ----------------------------------------------------------------
  useEffect(() => {
    const e = api;
    if (!e || !projectId) return;
    if (!chosen) {
      setGeometry(null);
      setModelError('');
      return;
    }
    let alive = true;
    void (async () => {
      try {
        const sidecar = `${chosen}.rig.json`;
        const rig = exists(sidecar) ? new TextDecoder().decode(await readProjectFile(projectId, sidecar)) : '';
        const bytes = await readProjectFile(projectId, chosen);
        if (/\.glb$/i.test(chosen)) {
          if (!alive) return;
          e.loadModelGlb(bytes, rig);
        } else {
          const text = new TextDecoder().decode(bytes);
          const parsed = JSON.parse(text) as { materials?: Record<string, Record<string, unknown>> };
          const images = new Set<string>();
          for (const m of Object.values(parsed.materials ?? {})) for (const k of IMAGE_KEYS) if (typeof m[k] === 'string') images.add(m[k]);
          const loaded = await Promise.all([...images].map(async (img) => [img, await readProjectFile(projectId, img)] as const));
          if (!alive) return;
          for (const [img, data] of loaded) e.addFile(img, data);
          e.loadModelDoc(text, rig);
        }
        setGeometry(JSON.parse(e.geometry()) as AnimatorPreviewGeometry);
        // Rest pose until the first evaluation.
        const rest = JSON.parse(e.frame()) as AnimatorPreviewFrame;
        setPose((p) => ({ positions: e.positions(), frame: rest, version: p.version + 1 }));
        setModelError('');
      } catch (err) {
        if (alive) {
          setGeometry(null);
          setModelError(`${chosen}: ${message(err)}`);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [api, projectId, chosen, exists]);

  // ---- controller (debounced while editing) -----------------------------------
  useEffect(() => {
    const e = api;
    if (!e || !projectId) return;
    let alive = true;
    const timer = setTimeout(() => {
      void (async () => {
        try {
          setIssues(JSON.parse(e.setController(content || '{}')) as string[]);
          const missing = (JSON.parse(e.requiredFiles()) as string[]).filter(exists);
          const files = await Promise.all(missing.map(async (f) => [f, await readProjectFile(projectId, f)] as const));
          if (!alive) return;
          for (const [f, data] of files) e.addFile(f, data);
        } catch (err) {
          if (alive) setIssues([message(err)]);
        }
        if (alive) setControllerVersion((v) => v + 1);
      })();
    }, 150);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [api, projectId, content, exists]);

  // ---- parameters -> engine ---------------------------------------------------
  const pushed = useRef<{ version: number; values: Record<string, PreviewValue> }>({ version: -1, values: {} });
  useEffect(() => {
    const e = api;
    if (!e) return;
    // A reloaded controller re-seeds its parameters: push every value again.
    const all = pushed.current.version !== controllerVersion;
    for (const [name, v] of Object.entries(values)) {
      if (!all && pushed.current.values[name] === v) continue;
      try {
        e.setParam(name, JSON.stringify(v));
      } catch {
        /* not compiled yet (a controller edit is pending) */
      }
    }
    pushed.current = { version: controllerVersion, values: { ...values } };
  }, [values, api, controllerVersion]);

  // ---- evaluation ---------------------------------------------------------------
  const timing = useCallback((layer: number, state: string): StateTiming => {
    const raw = engine.current?.stateTiming(layer, state);
    return (raw && raw !== 'null' ? JSON.parse(raw) : { length: 0, speed: 1, loop: true }) as StateTiming;
  }, []);

  // Lengths depend on parameters (blend trees, speed parameters) and edits.
  const line = useMemo<{ timeline: TransitionTimeline; src: StateTiming; dst: StateTiming } | null>(() => {
    if (!ready || target?.kind !== 'transition') return null;
    const src = timing(target.layer, target.from);
    const dst = timing(target.layer, target.to);
    return { timeline: transitionTimeline(target.t, src, dst), src, dst };
  }, [ready, target, timing, values, controllerVersion, geometry]);

  const stateCycle = useMemo(() => {
    if (!ready || target?.kind !== 'state') return 1;
    const t = timing(target.layer, target.state);
    const len = t.length > 1e-4 ? t.length : 1;
    return len / Math.max(Math.abs(t.speed || 1), 1e-3);
  }, [ready, target, timing, values, controllerVersion, geometry]);

  const duration = mode === 'live' ? Math.max(10, time) : target?.kind === 'transition' ? (line?.timeline.total ?? 1) : stateCycle;

  const show = useCallback(
    (frameText: string) => {
      const e = engine.current;
      if (!e) return;
      const frame = JSON.parse(frameText) as AnimatorPreviewFrame;
      setPose((p) => ({ positions: e.positions(), frame, version: p.version + 1 }));
      // Live transitions consume triggers: mirror that in the parameter list.
      const consumed = Object.entries(frame.params).filter(([k, v]) => typeof v === 'boolean' && values[k] !== undefined && values[k] !== v);
      if (consumed.length) {
        const next = { ...values, ...Object.fromEntries(consumed) };
        pushed.current = { ...pushed.current, values: { ...pushed.current.values, ...Object.fromEntries(consumed) } };
        onValues(next);
      }
    },
    [values, onValues],
  );

  const evaluate = useCallback(
    (t: number, step?: number) => {
      const e = engine.current;
      if (!e || !geometry) return;
      try {
        if (mode === 'live') show(step !== undefined ? e.update(step) : e.seek(t));
        else if (target?.kind === 'state') show(e.previewState(target.layer, target.state, stateNormalized(timing(target.layer, target.state), t, true)));
        else if (target?.kind === 'transition' && line) {
          const s = transitionSample(target.t, line.src, line.dst, line.timeline, t);
          show(e.previewTransition(target.layer, target.from, s.fromTime, target.to, s.toTime, s.weight));
        }
      } catch (err) {
        setPose((p) => ({ ...p, frame: { joints: p.frame?.joints ?? [], layers: [], params: {}, shown: { kind: 'live', time: 0 }, errors: [message(err)] } }));
      }
    },
    [geometry, mode, target, line, timing, show],
  );

  const timeRef = useRef(time);
  timeRef.current = time;

  // Selection mode: a new target starts from 0. Live mode: (re)starts with the model / mode.
  const targetKey = target ? (target.kind === 'state' ? `s:${target.layer}:${target.state}` : `t:${target.layer}:${target.from}:${target.to}`) : '';
  useEffect(() => {
    if (mode !== 'live') setTime(0);
  }, [targetKey, mode]);
  useEffect(() => {
    if (mode !== 'live') return;
    engine.current?.restart();
    setTime(0);
  }, [mode, chosen, controllerVersion]);

  // Selection mode draws whatever the clock / parameters / edits say.
  useEffect(() => {
    if (mode !== 'live') evaluate(time);
  }, [mode, time, evaluate]);
  // Live mode while paused: show parameter changes and reloads at once.
  useEffect(() => {
    if (mode === 'live' && !playing) evaluate(timeRef.current, 0);
  }, [mode, playing, evaluate]);

  // Playback clock (reads the latest settings through a ref).
  const clock = useRef({ speed, loop, duration, mode, evaluate });
  clock.current = { speed, loop, duration, mode, evaluate };
  useEffect(() => {
    if (!playing || !geometry) return;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const c = clock.current;
      const dt = Math.min(0.1, Math.max(0, now - last) / 1000) * c.speed;
      last = now;
      let t = timeRef.current + dt;
      if (c.mode === 'live') c.evaluate(t, dt);
      else if (t > c.duration) {
        if (c.loop) t = c.duration > 0 ? t % c.duration : 0;
        else {
          t = c.duration;
          setPlaying(false);
        }
      }
      timeRef.current = t;
      setTime(t);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, geometry]);

  const scrub = (t: number) => {
    if (mode === 'live') {
      setPlaying(false);
      evaluate(t);
    }
    timeRef.current = t;
    setTime(t);
  };

  // ---- view -------------------------------------------------------------------
  const frame = pose.frame;
  const errors = [...(modelError ? [modelError] : []), ...(frame?.errors ?? []), ...issues.filter((i) => !i.startsWith('warning'))];
  const liveInfo = mode === 'live' && frame
    ? frame.layers.map((l) => `${l.name}: ${l.state}${l.inTransition ? ` → ${l.nextState} ${Math.round(l.transitionProgress * 100)}%` : ` ${l.normalizedTime.toFixed(2)}`}`)
    : [];
  const label =
    mode === 'live'
      ? `${time.toFixed(2)} s`
      : target?.kind === 'transition'
        ? `${time.toFixed(2)} / ${duration.toFixed(2)} s`
        : `${(duration > 0 ? time / duration : 0).toFixed(2)} (${time.toFixed(2)} s)`;

  return (
    <section className="anim-preview" aria-label="Preview">
      <div className="anim-preview-head">
        <div className="anim-seg" role="tablist">
          <button type="button" className={mode === 'selection' ? 'is-active' : ''} onClick={() => setMode('selection')} title="Preview the selected state, blend tree or transition">
            Selection
          </button>
          <button type="button" className={mode === 'live' ? 'is-active' : ''} onClick={() => setMode('live')} title="Run the whole state machine with the preview parameters">
            State machine
          </button>
        </div>
        <div className="anim-preview-model">
          <SelectField value={chosen ?? undefined} placeholder="Pick a model…" options={models} onChange={setModel} />
        </div>
        <IconButton icon="eye" title="Show joints" active={showJoints} onClick={() => setShowJoints(!showJoints)} />
        <IconButton icon="refresh" title="Reload model and clips" onClick={() => setReload((r) => r + 1)} />
      </div>
      <div className="anim-preview-stage">
        {status || !chosen ? (
          <p className="anim-preview-msg">{status || 'Add a .glb or .model.json to the project to preview this controller.'}</p>
        ) : (
          <PreviewViewport geometry={geometry} positions={pose.positions} joints={frame?.joints ?? null} showJoints={showJoints} version={pose.version} />
        )}
        {(liveInfo.length > 0 || (mode === 'selection' && target)) && (
          <div className="anim-preview-info mono">
            {mode === 'live'
              ? liveInfo.map((l, i) => <div key={i}>{l}</div>)
              : target?.kind === 'transition'
                ? `${target.from} → ${target.to}`
                : target?.state}
          </div>
        )}
      </div>
      {mode === 'selection' && target?.kind === 'transition' && line && (
        <TransitionTimelineBar timeline={line.timeline} t={target.t} from={target.from} to={target.to} time={time} onScrub={scrub} />
      )}
      <div className="anim-preview-transport">
        <IconButton icon={playing ? 'pause' : 'play'} title={playing ? 'Pause' : 'Play'} onClick={() => setPlaying(!playing)} />
        <IconButton
          icon="restart"
          title="Restart"
          onClick={() => {
            if (mode === 'live') engine.current?.restart();
            scrub(0);
          }}
        />
        <input
          type="range"
          className="anim-preview-scrub"
          min={0}
          max={duration}
          step={0.001}
          value={Math.min(time, duration)}
          onChange={(e) => scrub(Number(e.target.value))}
          aria-label="Preview time"
        />
        <span className="anim-preview-time mono">{label}</span>
      </div>
      <div className="anim-preview-transport">
        <span className="muted">Speed</span>
        <SelectField value={String(speed)} options={SPEEDS.map((s) => ({ value: s, label: `${s}×` }))} onChange={(v) => setSpeed(Number(v))} />
        {mode === 'selection' && <Toggle checked={loop} onChange={setLoop} label="Loop" />}
        {mode === 'live' && target?.kind === 'state' && (
          <button
            type="button"
            className="anim-chip"
            title="Jump the running state machine to the selected state"
            onClick={() => {
              engine.current?.play(target.state, target.layer);
              if (!playing) evaluate(time, 0);
            }}
          >
            Play {target.state.split('/').pop()}
          </button>
        )}
        {mode === 'live' && target?.kind === 'transition' && (
          <button
            type="button"
            className="anim-chip"
            title="Cross-fade the running state machine into the destination"
            onClick={() => {
              const len = line?.timeline.duration ?? target.t.duration ?? 0.25;
              engine.current?.crossfade(target.to, target.layer, len);
              if (!playing) evaluate(time, 0);
            }}
          >
            Cross-fade to {target.to.split('/').pop()}
          </button>
        )}
      </div>
      {errors.length > 0 && (
        <div className="anim-preview-errors">
          {errors.slice(0, 4).map((e, i) => (
            <p key={i}>{e}</p>
          ))}
        </div>
      )}
    </section>
  );
}

/** Unity-style transition preview: source / destination bars, the blend window and a draggable playhead. */
function TransitionTimelineBar({
  timeline,
  t,
  from,
  to,
  time,
  onScrub,
}: {
  timeline: TransitionTimeline;
  t: Transition;
  from: string;
  to: string;
  time: number;
  onScrub: (t: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const total = Math.max(timeline.total, 1e-3);
  const pct = (s: number) => `${(Math.min(Math.max(s, 0), total) / total) * 100}%`;
  const at = (e: ReactPointerEvent) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    onScrub(Math.min(total, Math.max(0, ((e.clientX - r.left) / r.width) * total)));
  };
  return (
    <div
      ref={ref}
      className="anim-timeline"
      onPointerDown={(e) => {
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        at(e);
      }}
      onPointerMove={(e) => e.buttons && at(e)}
      title={`Exit ${t.hasExitTime ? (t.exitTime ?? 1).toFixed(2) : '—'} · duration ${timeline.duration.toFixed(2)} s · offset ${(t.offset ?? 0).toFixed(2)}`}
    >
      <div className="anim-timeline-blend" style={{ left: pct(timeline.start), width: `calc(${pct(timeline.end)} - ${pct(timeline.start)})` }} />
      <div className="anim-timeline-bar is-source" style={{ left: 0, width: pct(timeline.sourceEnd) }}>
        <span>{from.split('/').pop()}</span>
      </div>
      <div className="anim-timeline-bar is-dest" style={{ left: pct(timeline.start), right: 0 }}>
        <span>
          {to.split('/').pop()}
          {t.offset ? ` @${t.offset.toFixed(2)}` : ''}
        </span>
      </div>
      {t.hasExitTime && <div className="anim-timeline-mark" style={{ left: pct(timeline.start) }} title="Exit time" />}
      <div className="anim-timeline-head" style={{ left: pct(time) }} />
    </div>
  );
}

/** Preview controls of the parameter list (values, trigger fire, apply as default). */
export function PreviewParam({
  type,
  value,
  range,
  onChange,
  onApply,
  isDefault,
}: {
  type: 'float' | 'int' | 'bool' | 'trigger';
  value: PreviewValue | undefined;
  range: [number, number];
  onChange: (v: PreviewValue) => void;
  onApply: () => void;
  isDefault: boolean;
}) {
  return (
    <div className="anim-param-preview">
      <span className="anim-param-preview-label" title="Preview value (does not change the controller)">
        preview
      </span>
      {(type === 'float' || type === 'int') && (
        <>
          <input
            type="range"
            min={range[0]}
            max={range[1]}
            step={type === 'int' ? 1 : (range[1] - range[0]) / 200 || 0.01}
            value={typeof value === 'number' ? value : 0}
            onChange={(e) => onChange(type === 'int' ? Math.round(Number(e.target.value)) : Number(e.target.value))}
          />
          <NumberField value={typeof value === 'number' ? value : 0} integer={type === 'int'} step={type === 'int' ? 1 : 0.05} onChange={onChange} />
        </>
      )}
      {type === 'bool' && <Toggle checked={value === true} onChange={onChange} />}
      {type === 'trigger' && (
        <button type="button" className={`anim-chip${value === true ? ' is-armed' : ''}`} onClick={() => onChange(true)} title="Fire the trigger (consumed by the next transition that uses it)">
          {value === true ? 'armed' : 'fire'}
        </button>
      )}
      {type !== 'trigger' && (
        <IconButton icon="check" title={isDefault ? 'This is the default value' : 'Use as the default value'} disabled={isDefault} onClick={onApply} />
      )}
    </div>
  );
}
